import { avisarErro } from '@/components/common/Toast';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

export interface DownloadOrShareOptions {
  blob: Blob;
  filename: string;
  mimeType: string;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      if (base64) resolve(base64);
      else reject(new Error('Falha ao converter blob para base64'));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function downloadInBrowser(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.position = 'fixed';
  a.style.top = '-1000px';
  a.style.left = '-1000px';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

/**
 * No web: dispara download via <a download>.
 * No app (Capacitor): grava o arquivo no Filesystem e abre o Share sheet (Salvar em Arquivos, etc.).
 */
export async function downloadOrShareFile({
  blob,
  filename,
  mimeType: _mimeType
}: DownloadOrShareOptions): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    downloadInBrowser(blob, filename);
    return;
  }

  try {
    const base64 = await blobToBase64(blob);
    const path = filename.replace(/[^a-zA-Z0-9._-]/g, '_');

    const { uri } = await Filesystem.writeFile({
      path,
      data: base64,
      directory: Directory.Cache
    });

    await Share.share({
      url: uri,
      title: filename,
      dialogTitle: 'Salvar ou compartilhar'
    });
  } catch (error) {
    console.error('downloadOrShareFile:', error);
    const message = error instanceof Error ? error.message : 'Erro ao salvar ou compartilhar o arquivo';
    avisarErro(message);
    throw error;
  }
}

/**
 * COMPARTILHAR (1.4): o arquivo vai como ARQUIVO para a folha de compartilhar do sistema (WhatsApp, e-mail, Drive…).
 * - No aplicativo: grava no cache e abre o Share nativo com `files` (o WhatsApp recebe o PDF, e não um link).
 * - No navegador: o compartilhamento do navegador quando ele aceita arquivos (`navigator.canShare`); senão, baixa.
 */
export function podeCompartilhar(): boolean {
  if (Capacitor.isNativePlatform()) return true;
  try {
    const teste = new File([new Blob(['x'], { type: 'application/pdf' })], 'teste.pdf', { type: 'application/pdf' });
    return typeof navigator.canShare === 'function' && navigator.canShare({ files: [teste] });
  } catch { return false; }
}

export async function compartilharArquivo({ blob, filename, mimeType, titulo }: DownloadOrShareOptions & { titulo?: string }): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    const base64 = await blobToBase64(blob);
    const { uri } = await Filesystem.writeFile({ path: filename.replace(/[^a-zA-Z0-9._-]/g, '_'), data: base64, directory: Directory.Cache });
    await Share.share({ title: titulo ?? filename, files: [uri], dialogTitle: 'Compartilhar' });
    return;
  }
  const arquivo = new File([blob], filename, { type: mimeType || blob.type });
  if (typeof navigator.canShare === 'function' && navigator.canShare({ files: [arquivo] })) {
    try { await navigator.share({ files: [arquivo], title: titulo ?? filename }); return; }
    catch (e) { if ((e as { name?: string }).name === 'AbortError') return; /* sem compartilhar: baixa */ }
  }
  downloadInBrowser(blob, filename);
}

